import {
	IRegEdSubmissionInput,
	IRegEdSubmissionMetadata,
	useAccountOfficerFilesRegEd,
	useAccountUsesRegEd,
	useRegEdIntegrationQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { accountAllowsComplianceBypass, getAccountComplianceEmail, isValidEmail } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { LoadingSpinner } from '../../LoadingSpinner';
import { baseStyleSheet } from '../../../styles/styles';
import { lastUsedComplianceRecipientEmail } from '../../campaigns/CampaignDetailsSummary';
import { ComplianceIcon } from '../../svgs/icons/ComplianceIcon';
import { Input } from '../../../components-v2/Input';
import { getCampaignNames } from './campaignNaming';
import { RegEdComplianceApprovalPrompt } from './RegEdComplianceApprovalPrompt';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	campaignType?: CampaignType;
	disabled?: boolean;
	isLoading?: boolean;
	onScheduleCompliance(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string): void;
	onScheduleWithoutCompliance(e: React.MouseEvent<HTMLElement>): void;
	/** Opt-in for the RegEd flow — the caller submits the returned metadata via the submissions endpoint. */
	onScheduleRegEdCompliance?(e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionInput): void;
	/** The platforms the post targets, which answers the RegEd platform question. */
	regEdSocialProviders?: readonly Api.SocialMediaType[];
	/** Seed the RegEd form when resubmitting a revision (prior filing's format + answers). */
	regEdInitialFormat?: string;
	regEdInitialAnswers?: Record<string, unknown>;
	regEdRevisesApprovalRequestId?: string | null;
	regEdOwnerUserId?: string | null;
	/** Fires on every RegEd form edit so the caller can hold a draft that survives back-navigation. */
	onRegEdFormStateChange?(metadata: IRegEdSubmissionMetadata): void;
	styles?: StyleDeclarationValue[];
}

export const ComplianceApprovalPrompt: React.FC<IProps> = inject(ImpersonationContextKey)(
	observer(
		({
			campaignType,
			disabled,
			impersonationContext,
			isLoading,
			onScheduleCompliance,
			onScheduleWithoutCompliance,
			onScheduleRegEdCompliance,
			regEdSocialProviders,
			regEdInitialFormat,
			regEdInitialAnswers,
			regEdRevisesApprovalRequestId,
			regEdOwnerUserId,
			onRegEdFormStateChange,
			styles = [],
		}) => {
			const userSession = useUserSession();
			// This prompt is shared by every compliance flow — probe RegEd only for callers that opt in.
			const usesRegEd = useAccountUsesRegEd(impersonationContext, { enabled: !!onScheduleRegEdCompliance });
			// Same query as the gate above (react-query dedupes); its loading state avoids flashing the wrong prompt.
			const { isLoading: regEdProbeLoading } = useRegEdIntegrationQuery({
				impersonationContext,
				enabled: !!onScheduleRegEdCompliance,
			});
			const officerFilesRegEd = useAccountOfficerFilesRegEd(impersonationContext, {
				enabled: !!onScheduleRegEdCompliance,
			});

			const isImpersonating = impersonationContext?.isValid ?? false;
			const accountId = isImpersonating ? impersonationContext.account : userSession.account;
			// Admin-set account-level recipient: pre-filled and not editable on individual sends
			// (https://github.com/Real-Magic/Levitate/issues/18484).
			const accountComplianceEmail = getAccountComplianceEmail(userSession, impersonationContext);
			const [sendWithComplianceEmail, setSendWithComplianceEmail] = React.useState<string>(() => {
				return (
					accountComplianceEmail ??
					localStorage.getItem(
						!isImpersonating ? lastUsedComplianceRecipientEmail : `${accountId}-${lastUsedComplianceRecipientEmail}`
					) ??
					''
				);
			});

			const { ctaName: campaignCtaName, displayName: campaignDisplayName } = getCampaignNames(campaignType);

			const checkComplianceEmail = () => {
				if (sendWithComplianceEmail.length > 0 && !isValidEmail(sendWithComplianceEmail)) {
					toast.error(`Please enter a valid email address`);
					return false;
				}
				return true;
			};

			if (onScheduleRegEdCompliance && regEdProbeLoading) {
				return (
					<div className={css(styleSheet.container, styleSheet.regEdProbePlaceholder, ...styles)}>
						<LoadingSpinner type='large' />
					</div>
				);
			}
			// The advisor fills nothing here: a submission written from this side wins over the portal when the
			// compliance PDF lands, and the officer would never be emailed.
			if (onScheduleRegEdCompliance && usesRegEd && officerFilesRegEd) {
				return (
					<div className={`${css(styleSheet.container, ...styles)} compliance-approval-prompt`}>
						<div className={css(styleSheet.iconContainer)}>
							<ComplianceIcon className={css(styleSheet.icon)} />
						</div>
						<div>
							{accountComplianceEmail ? 'Your compliance officer files this' : 'No compliance officer is set up'}
						</div>
						<div className={css(styleSheet.detailMessage)}>
							{accountComplianceEmail
								? `We will email your firm's compliance officer a link to review this ${campaignDisplayName} and file it with your compliance system. Schedule it now and it will be held in a pending state until they decide.`
								: `This account files compliance through a reviewer, but no compliance email address is set — so nobody would be sent this ${campaignDisplayName}. Ask your administrator to add one before scheduling.`}
						</div>
						<div className={css(baseStyleSheet.verticalStack, styleSheet.stack)}>
							<Button
								disabled={disabled || isLoading || !accountComplianceEmail}
								onClick={(e: React.MouseEvent<HTMLElement>) => onScheduleCompliance(e, undefined)}
								isLoading={isLoading}
							>
								{`Schedule ${campaignCtaName} with Compliance Pending`}
							</Button>
						</div>
					</div>
				);
			}
			const canSendWithoutCompliance =
				!isImpersonating && accountAllowsComplianceBypass(userSession, impersonationContext, usesRegEd);

			if (onScheduleRegEdCompliance && usesRegEd) {
				return (
					<RegEdComplianceApprovalPrompt
						impersonationContext={impersonationContext}
						campaignType={campaignType}
						socialProviders={regEdSocialProviders}
						disabled={disabled}
						isLoading={isLoading}
						initialFormat={regEdInitialFormat}
						initialAnswers={regEdInitialAnswers}
						revisesApprovalRequestId={regEdRevisesApprovalRequestId}
						ownerUserId={regEdOwnerUserId}
						onFormStateChange={onRegEdFormStateChange}
						onScheduleRegEdCompliance={onScheduleRegEdCompliance}
						onScheduleWithoutCompliance={canSendWithoutCompliance ? onScheduleWithoutCompliance : undefined}
						styles={styles}
					/>
				);
			}

			return (
				<div className={`${css(styleSheet.container, ...styles)} compliance-approval-prompt`}>
					<div className={css(styleSheet.iconContainer)}>
						<ComplianceIcon className={css(styleSheet.icon)} />
					</div>
					<div>Do you need approval from compliance officer?</div>
					<div className={css(styleSheet.detailMessage)}>
						{`We will email you a copy of the ${campaignDisplayName} and you can work with your compliance officer to get approved. In the meantime, you can schedule this ${campaignDisplayName}, and it'll be held in a pending state until you come back and mark it as approved.`}
					</div>
					<div className={css(baseStyleSheet.verticalStack, styleSheet.stack)}>
						<div className={css(styleSheet.complianceInput)}>
							<Input
								id='user-emailAddress'
								placeholder='Please enter Compliance Email'
								type='text'
								disabled={!!accountComplianceEmail}
								title={
									accountComplianceEmail
										? 'This email address is set by your account administrator and cannot be changed.'
										: undefined
								}
								onBlur={() => {
									if (!checkComplianceEmail()) {
										return;
									}
									localStorage.setItem(lastUsedComplianceRecipientEmail, sendWithComplianceEmail);
								}}
								onChange={ev => {
									setSendWithComplianceEmail(ev.target.value);
								}}
								value={sendWithComplianceEmail}
							/>
						</div>
						<Button
							disabled={disabled || isLoading}
							onClick={(e: React.MouseEvent<HTMLElement>) => {
								if (!checkComplianceEmail()) {
									return;
								}
								onScheduleCompliance(e, sendWithComplianceEmail);
							}}
							isLoading={isLoading}
						>
							{`Schedule ${campaignCtaName} with Compliance Pending`}
						</Button>

						{canSendWithoutCompliance ? (
							<Button
								disabled={disabled || isLoading}
								variant='secondary'
								onClick={onScheduleWithoutCompliance}
								isLoading={isLoading}
							>
								{`Send ${campaignCtaName} without Compliance`}
							</Button>
						) : null}
					</div>
				</div>
			);
		}
	)
);
